import { getPhotosWithPath, getPhotoUrl } from '@bridebook/web/app-shared/lib/supplier/utils';
import { isCustomSupplier } from '@bridebook/web/lib/supplier/hooks/query/use-supplier';
import { getSupplierAddressText } from '@bridebook/web/lib/wedding-website/utils/get-supplier-address-text';
import { isVenueSection } from '@bridebook/web/lib/wedding-website/utils/sections-typeguards';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { useWeddingWebsiteData } from '../../data/data-context';
import { AUTOPLAY_INTERVAL, VENUE_CAROUSEL_IMAGE } from '../../config/constants';
import { VenueCarousel } from '../../sections/venue/venue-carousel';
import { MotifOrnament, MotifSectionHookProps } from './motif-section-hooks';

export const MotifVenue = (hookProps: MotifSectionHookProps) => {
  const { t } = useTranslation('weddingWebsite');
  const { mainSupplier, mainSupplierPhotos, website } = useWeddingWebsiteData();
  const venueSection = website.sections.find(isVenueSection);
  const hasBBSupplier = !!mainSupplier && !isCustomSupplier(mainSupplier);
  const showVenuePhotos = venueSection?.data.useVenuePhotos ?? false;

  const photoUrls = useMemo(
    () => getPhotosWithPath(mainSupplierPhotos).map(getPhotoUrl).slice(0, 5),
    [mainSupplierPhotos],
  );

  // Reuse the same venue carousel as the Classic/Split layouts (Embla, autoplay, dots/arrows). It is self-contained
  // and not styled via bb-ww classes — the theme just provides the section frame around it.
  const showCarousel = hasBBSupplier && showVenuePhotos && photoUrls.length > 0;

  return (
    <section {...hookProps} className="bb-ww__section bb-ww__section--venue">
      <MotifOrnament />
      <h2 className="bb-ww__section-title">{t('previewMode.tile.ourVenue.title')}</h2>
      <p className="bb-ww__body">{mainSupplier?.name}</p>
      <p className="bb-ww__meta">
        {mainSupplier
          ? getSupplierAddressText(mainSupplier)
          : t('previewMode.tile.venue.toBeConfirmed')}
      </p>
      {showCarousel && (
        // Full content-width so it never reads as a centered box clashing with left-aligned text. The carousel is
        // `w-full` and keeps its aspect ratio from imageWidth/imageHeight.
        <div className="bb-ww__gallery" style={{ width: '100%' }}>
          <VenueCarousel
            images={photoUrls}
            imageWidth={VENUE_CAROUSEL_IMAGE.width}
            imageHeight={VENUE_CAROUSEL_IMAGE.height}
            autoplayInterval={AUTOPLAY_INTERVAL.venue}
          />
        </div>
      )}
    </section>
  );
};
